import { Platform } from 'react-native';
import env from './env';
import { logger } from '@/src/core/utils';
import {
  configureRevenueCatWeb,
  loginRevenueCatWeb,
  checkEntitlementsWeb,
  getOfferingsWeb,
  purchasePackageWeb,
} from './revenuecatWeb';

/**
 * RevenueCat configuration
 *
 * Initializes RevenueCat SDK with platform-specific API keys.
 * Falls back gracefully if the SDK is not installed (dev/Expo Go).
 */

let Purchases: any = null;

export async function configureRevenueCat(userId?: string): Promise<void> {
  if (Platform.OS === 'web') {
    await configureRevenueCatWeb(userId);
    return;
  }

  const apiKey = Platform.OS === 'ios'
    ? env.revenuecatAppleKey
    : env.revenuecatGoogleKey;

  if (!apiKey) {
    logger.debug('RevenueCat', 'No API key configured, skipping initialization');
    return;
  }

  try {
    Purchases = require('react-native-purchases').default;
    await Purchases.configure({ apiKey });

    if (userId) {
      await Purchases.logIn(userId);
    }

    logger.info('RevenueCat', 'Configured successfully', { platform: Platform.OS });
  } catch (error) {
    logger.warn('RevenueCat', 'Failed to configure (SDK may not be installed)', { error });
    Purchases = null;
  }
}

export async function loginRevenueCat(userId: string): Promise<void> {
  if (Platform.OS === 'web') {
    await loginRevenueCatWeb(userId);
    return;
  }
  if (!Purchases) return;
  try {
    await Purchases.logIn(userId);
    logger.debug('RevenueCat', 'User logged in', { userId });
  } catch (error) {
    logger.warn('RevenueCat', 'Failed to log in user', { error });
  }
}

export async function logoutRevenueCat(): Promise<void> {
  if (!Purchases) return;
  try {
    await Purchases.logOut();
    logger.debug('RevenueCat', 'User logged out');
  } catch (error) {
    logger.warn('RevenueCat', 'Failed to log out user', { error });
  }
}

export async function checkEntitlements(): Promise<boolean> {
  if (Platform.OS === 'web') return checkEntitlementsWeb();
  if (!Purchases) return false;
  try {
    const customerInfo = await Purchases.getCustomerInfo();
    const isPro = customerInfo.entitlements.active['pro'] !== undefined;
    logger.debug('RevenueCat', 'Entitlement check', { isPro });
    return isPro;
  } catch (error) {
    logger.warn('RevenueCat', 'Failed to check entitlements', { error });
    return false;
  }
}

export async function getOfferings(): Promise<any> {
  if (Platform.OS === 'web') return getOfferingsWeb();
  if (!Purchases) return null;
  try {
    const offerings = await Purchases.getOfferings();
    return offerings;
  } catch (error) {
    logger.warn('RevenueCat', 'Failed to get offerings', { error });
    return null;
  }
}

export async function purchasePackage(pkg: any): Promise<boolean> {
  if (Platform.OS === 'web') return purchasePackageWeb(pkg);
  if (!Purchases) return false;
  try {
    const { customerInfo } = await Purchases.purchasePackage(pkg);
    return customerInfo.entitlements.active['pro'] !== undefined;
  } catch (error: any) {
    if (error.userCancelled) {
      logger.debug('RevenueCat', 'User cancelled purchase');
      return false;
    }
    logger.error('RevenueCat', 'Purchase failed', { error });
    throw error;
  }
}

export async function restorePurchases(): Promise<boolean> {
  if (!Purchases) return false;
  try {
    const customerInfo = await Purchases.restorePurchases();
    return customerInfo.entitlements.active['pro'] !== undefined;
  } catch (error) {
    logger.error('RevenueCat', 'Restore purchases failed', { error });
    throw error;
  }
}

export { Purchases };
